import { useCallback, useEffect } from 'react'
import { IntercomProvider, useIntercom } from 'react-use-intercom'

import { PageMetadata } from '@/pages/_app'

type CustomIntercomProviderProps = {
  children: React.ReactNode
  appId: string
  pageMetadata: PageMetadata
  shouldInitializeUserTracking: boolean
}

const FS_LAYOUT_PADDING = 60

export function CustomIntercomProvider({
  children,
  appId,
  pageMetadata,
  shouldInitializeUserTracking,
}: CustomIntercomProviderProps) {
  const isFullScreenLayout = pageMetadata?.hasFullscreenLayout
  return (
    <IntercomProvider
      appId={appId}
      autoBoot={true}
      autoBootProps={{
        verticalPadding: isFullScreenLayout ? FS_LAYOUT_PADDING : 0,
      }}
      shouldInitialize={shouldInitializeUserTracking}
    >
      <IntercomResizer isFullScreenLayout={isFullScreenLayout}>
        {children}
      </IntercomResizer>
    </IntercomProvider>
  )
}

export default CustomIntercomProvider

type IntercomResizerProps = {
  isFullScreenLayout: boolean
  children: React.ReactNode
}

function IntercomResizer({
  isFullScreenLayout,
  children,
}: IntercomResizerProps) {
  const { boot, update, shutdown } = useIntercom()

  const updateIntercom = useCallback(() => {
    const verticalPadding = isFullScreenLayout ? FS_LAYOUT_PADDING : 0
    update({ verticalPadding })
  }, [isFullScreenLayout, update])

  useEffect(() => {
    if (isFullScreenLayout) {
      boot()
      updateIntercom()
    }

    return () => shutdown()
  }, [boot, updateIntercom, shutdown, isFullScreenLayout])

  return children
}
